// PE-6104 / PE-6106 — patient portal self-scheduling BFF client (returning-patient /book-appointment only).
// PE-6156 — guest/new-patient sign-up booking must use GuestSelfSchedulingService (PE2 APIs), not this service.
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ReturningPatientAppointmentEmailFields,
  buildReturningPatientAppointmentEmailBody,
} from '../shared/email/appointment-confirmation-email.builder';
import { buildReturningPatientAppointmentCancellationEmailBody } from '../shared/email/appointment-cancellation-email.builder';
import { AppointmentViewModel } from '../shared/models/appointment-view-model';
import { prepareAppointmentViewModelIds } from '../shared/utils/appointment-view-model.builder';
import { TenantService } from './tenant.service';
import { SnackbarService } from './snackbar.service';
import { resolveHttpErrorMessage } from '../utils/http-connection-error.util';

/** PE-6229 — cancel payload for BFF → Qore deleteAppointment */
export interface SelfSchedulingCancelPayload {
  tenantid: string;
  patientid: string;
  appointmentid?: string;
  /**
   * Single detail id for cancel (portal entity form).
   * Built from search SCHAppointmentUID digits + tenant suffix — do not also send SCHAppointmentUID.
   */
  appointmentdetailsid: string;
  locationid?: string;
  locationname?: string;
  userid?: string;
  emrmappingid?: string;
}

export interface AppointmentConfirmationMailData {
  tenantid: string;
  toperson: string;
  cc: string;
  subject: string;
  body: string;
}

@Injectable({ providedIn: 'root' })
export class SelfSchedulingService {
  private readonly http = inject(HttpClient);
  private readonly tenantService = inject(TenantService);
  private readonly snackbar = inject(SnackbarService);

  // PE-00 — avoid duplicate toasts when calendar-dates + slots fail together
  private lastSchedulingErrorToastMessage = '';
  private lastSchedulingErrorToastAt = 0;
  private readonly schedulingErrorToastDedupeMs = 2000;

  private get baseUrl(): string {
    return `${this.tenantService.getApiUrl()}/selfscheduling`;
  }

  private unwrapData<T>(response: unknown): T {
    const body = response as { data?: T; success?: string | boolean };
    if (body?.data !== undefined) {
      return body.data as T;
    }
    return response as T;
  }

  // PE-00 — HTTP 200 business failure (e.g. scheduling auth) must surface to the user
  private isSchedulingBusinessFailure(response: unknown): boolean {
    const body = response as { success?: string | boolean };
    return body?.success === false || body?.success === 'false';
  }

  // PE-00 — prefer API message; fallback when self-scheduling is temporarily unavailable
  private getSchedulingBusinessFailureMessage(response: unknown, fallback: string): string {
    const message = (response as { message?: string })?.message?.trim();
    return message || fallback;
  }

  // PE-00 — convert success:false payload into an error Observable (single toast via catchError)
  private assertSchedulingSuccess<T>(
    response: unknown,
    fallback: string
  ): T {
    if (this.isSchedulingBusinessFailure(response)) {
      const message = this.getSchedulingBusinessFailureMessage(response, fallback);
      throw { error: { message } };
    }
    return this.unwrapData<T>(response);
  }

  // PE-00 — one toast when calendar-dates and slots both return the same failure
  private showSchedulingErrorOnce(message: string): void {
    const now = Date.now();
    if (
      message === this.lastSchedulingErrorToastMessage &&
      now - this.lastSchedulingErrorToastAt < this.schedulingErrorToastDedupeMs
    ) {
      return;
    }
    this.lastSchedulingErrorToastMessage = message;
    this.lastSchedulingErrorToastAt = now;
    this.snackbar.showError(message);
  }

  getLocations(tenantid: string): Observable<unknown> {
    return this.http
      .get<unknown>(`${this.baseUrl}/locations`, { params: { tenantid } })
      .pipe(map((res) => this.unwrapData(res)));
  }

  getProviders(tenantid: string): Observable<unknown> {
    return this.http
      .get<unknown>(`${this.baseUrl}/providers`, { params: { tenantid } })
      .pipe(map((res) => this.unwrapData(res)));
  }

  getAppointmentTypes(tenantid: string): Observable<unknown> {
    return this.http
      .get<unknown>(`${this.baseUrl}/appointment-types`, { params: { tenantid } })
      .pipe(map((res) => this.unwrapData(res)));
  }

  getTenantSettings(tenantid: string): Observable<unknown> {
    return this.http
      .get<unknown>(`${this.baseUrl}/tenant-settings`, { params: { tenantid } })
      .pipe(map((res) => this.unwrapData(res)));
  }

  // PE-6177 — patient's own provider (PVUID) for OwnProviderAppointmentTypes lock
  getPatientOwnProvider(
    tenantid: string,
    patientid: string
  ): Observable<{ providerId?: string; providerName?: string }> {
    return this.http
      .get<unknown>(`${this.baseUrl}/patient-own-provider`, {
        params: { tenantid, patientid },
      })
      .pipe(
        map((res) => this.unwrapData<{ providerId?: string; providerName?: string }>(res)),
        catchError(() => of({ providerId: '', providerName: '' }))
      );
  }

  getCalendarDates(params: Record<string, string>): Observable<unknown> {
    const fallback =
      'Self-scheduling is temporarily unavailable. Please try again later.';
    return this.http.get<unknown>(`${this.baseUrl}/calendar-dates`, { params }).pipe(
      // PE-00 — toast API message when success is false (e.g. scheduling auth failed)
      map((res) => this.assertSchedulingSuccess(res, fallback)),
      catchError((err) => {
        // PE-6155 — HTTP errors toasted by interceptor; keep toast for success:false synthetic errors
        if (typeof (err as { status?: number })?.status !== 'number') {
          this.showSchedulingErrorOnce(resolveHttpErrorMessage(err, fallback));
        }
        return throwError(() => err);
      })
    );
  }

  getSlots(params: Record<string, string>): Observable<unknown> {
    const fallback =
      'Self-scheduling is temporarily unavailable. Please try again later.';
    return this.http.get<unknown>(`${this.baseUrl}/slots`, { params }).pipe(
      // PE-00 — toast API message when success is false (e.g. scheduling auth failed)
      map((res) => this.assertSchedulingSuccess(res, fallback)),
      catchError((err) => {
        // PE-6155 — HTTP errors toasted by interceptor; keep toast for success:false synthetic errors
        if (typeof (err as { status?: number })?.status !== 'number') {
          this.showSchedulingErrorOnce(resolveHttpErrorMessage(err, fallback));
        }
        return throwError(() => err);
      })
    );
  }

  // PE-6106 — pre-book slot lock check (Self-Scheduling-App confirmSlotInfo)
  getSlotStatus(params: Record<string, string>): Observable<unknown> {
    return this.http.get<unknown>(`${this.baseUrl}/slot-status`, { params }).pipe(
      map((res) => this.unwrapData(res)),
      catchError((err) => throwError(() => err))
    );
  }

  // Check selected slot against EMR Block Time before book
  checkSlotBlock(params: Record<string, string>): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/check-slot-block`, null, { params }).pipe(
      map((res) => this.unwrapData(res)),
      catchError((err) => throwError(() => err))
    );
  }

  bookAppointment(payload: AppointmentViewModel): Observable<unknown> {
    // PE-6106 — QPrime appointment.service prepareAppointmentData: assign IDs before save
    const prepared = prepareAppointmentViewModelIds(payload);
    // PE-6155 — HTTP errors toasted by connectionErrorInterceptor
    return this.http.post<unknown>(`${this.baseUrl}/bookappointment`, prepared);
  }

  // PE-6229 / PE-6231 — cancel self-scheduled appointment (BFF frees QSuite slot via deleteAppointment)
  cancelAppointment(payload: SelfSchedulingCancelPayload): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/cancelappointment`, payload).pipe(
      map((res) => this.assertSchedulingSuccess(res, 'Unable to cancel appointment. Please try again.')),
      catchError((err) => {
        if (typeof (err as { status?: number })?.status !== 'number') {
          this.snackbar.showError(resolveHttpErrorMessage(err, 'Unable to cancel appointment. Please try again.'));
        }
        return throwError(() => err);
      })
    );
  }

  // PE-6105 — server-side reCAPTCHA verification (Self-Scheduling-App verifyToken via BFF)
  verifyRecaptchaToken(token: string, tenantid: string): Observable<unknown> {
    return this.http
      .post<unknown>(`${this.baseUrl}/validate-captcha`, null, {
        params: { response: token, tenantid },
      })
      .pipe(map((res) => this.unwrapData(res)));
  }

  // PE-6108 — BFF proxies to auth sendMailPostCall (avoids browser CORS on sessionid)
  sendAppointmentConfirmationMail(
    tenantid: string,
    toEmail: string,
    tenantName: string,
    fields: Omit<ReturningPatientAppointmentEmailFields, 'tenantName'>
  ): Observable<unknown> {
    const recipient = toEmail?.trim();
    if (!recipient || !tenantid?.trim()) {
      return of(null);
    }

    const mailData: AppointmentConfirmationMailData = {
      tenantid: tenantid.trim(),
      toperson: recipient,
      cc: '',
      subject: `${tenantName} : Appointment Confirmation`,
      body: buildReturningPatientAppointmentEmailBody({
        ...fields,
        tenantName,
      }),
    };

    return this.http.post<unknown>(`${this.baseUrl}/send-confirmation-email`, mailData).pipe(
      catchError((err) => {
        console.error('PE-6108: failed to send appointment confirmation email', err);
        return of(null);
      })
    );
  }

  // PE-6229 — same BFF mail proxy as confirmation; cancellation subject/body from builder
  sendAppointmentCancellationMail(
    tenantid: string,
    toEmail: string,
    tenantName: string,
    fields: Omit<ReturningPatientAppointmentEmailFields, 'tenantName'>
  ): Observable<unknown> {
    const recipient = toEmail?.trim();
    if (!recipient || !tenantid?.trim()) {
      return of(null);
    }

    const mailData: AppointmentConfirmationMailData = {
      tenantid: tenantid.trim(),
      toperson: recipient,
      cc: '',
      subject: `${tenantName} : Appointment Cancellation`,
      body: buildReturningPatientAppointmentCancellationEmailBody({
        ...fields,
        tenantName,
      }),
    };

    return this.http.post<unknown>(`${this.baseUrl}/send-confirmation-email`, mailData).pipe(
      catchError((err) => {
        console.error('PE-6229: failed to send appointment cancellation email', err);
        return of(null);
      })
    );
  }

  // PE-6108 — (___) ___-____ phone display for confirmation email
  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return phoneNumber;
    }

    const cleaned = phoneNumber.replace(/\D/g, '');
    const mask = '(999) 000-0000';
    let formattedNumber = '';
    let maskIndex = 0;
    let numberIndex = 0;

    while (maskIndex < mask.length && numberIndex < cleaned.length) {
      if (mask[maskIndex] === '9' || mask[maskIndex] === '0') {
        formattedNumber += cleaned[numberIndex];
        numberIndex++;
      } else {
        formattedNumber += mask[maskIndex];
      }
      maskIndex++;
    }

    return formattedNumber;
  }
}
